import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PayPal payment button — supports:
 *   purchaseType: "course" | "subscription_month" | "book" | "generic"
 *   courseSlug:   required if purchaseType === "course"
 *   amount:       required for generic; optional override for others
 *   authToken:    Bearer token of the logged-in member (required)
 *   onSuccess({order_id, status}): called after successful capture
 *   onError(err)
 */
export default function PayPalPayButton({
  purchaseType,
  courseSlug,
  amount,
  description,
  authToken,
  onSuccess,
  onError,
  disabled = false,
  style = { layout: "horizontal", color: "gold", shape: "rect", label: "paypal", tagline: false, height: 45 },
}) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/paypal/config`);
        if (data?.client_id) setConfig(data);
      } catch {
        // silent fail — button just won't render
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="h-[45px] bg-[#F4EDE0] animate-pulse" data-testid="paypal-loading" />
    );
  }
  if (!config?.client_id) {
    return null; // PayPal not configured — hide silently, Stripe fallback still visible
  }

  const authHdr = () => ({ Authorization: `Bearer ${authToken}` });

  return (
    <div data-testid="paypal-button-container" className={disabled ? "opacity-50 pointer-events-none" : ""}>
      <PayPalScriptProvider
        options={{
          "client-id": config.client_id,
          currency: config.currency || "EUR",
          intent: "capture",
          "enable-funding": "paylater,card",
        }}
      >
        <PayPalButtons
          style={style}
          disabled={disabled}
          createOrder={async () => {
            try {
              const { data } = await axios.post(
                `${API}/paypal/create-order`,
                {
                  purchase_type: purchaseType,
                  course_slug: courseSlug || null,
                  amount: amount || null,
                  description: description || null,
                  origin_url: window.location.origin,
                },
                { headers: authHdr() }
              );
              return data.order_id;
            } catch (err) {
              onError?.(err);
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const { data: capture } = await axios.post(
                `${API}/paypal/capture-order`,
                { order_id: data.orderID },
                { headers: authHdr() }
              );
              onSuccess?.(capture);
            } catch (err) {
              onError?.(err);
            }
          }}
          onError={(err) => onError?.(err)}
          onCancel={() => { /* silent */ }}
        />
      </PayPalScriptProvider>
    </div>
  );
}
